/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

AnalyticsService.$inject = [
    '$document',
    '$http',
    '$interval',
    '$location',
    '$log',
    '$q',
    '$state',
    '$window',
    'ProductApiConsumerService',
    'StorageService',
    'UtilService',
    'UUIDService',
    'GeoService',
];
function AnalyticsService(
    $document,
    $http,
    $interval,
    $location,
    $log,
    $q,
    $state,
    $window,
    ProductApiConsumerService,
    StorageService,
    UtilService,
    UUIDService,
    GeoService
) {
    let _pub = {};
    let getUserStatus = getUserStatusPlaceholder;
    let getUserDetails = getUserDetailsPlaceholder;
    const defaultProductParams = [
        {key: 'brand', default: null},
        {key: 'category', source: 'category_name', default: null},
        {key: 'category_id', source: 'category_id', default: null, include_in_event: true},
        {key: 'enervee_id', source: 'id', default: null},
        {key: 'enervee_score', default: null},
        {key: 'fuel_type', source: 'fuel_units', default: ''},
        {key: 'image_url', default: null},
        {key: 'key_attributes', source: 'top_specs_map', default: {}},
        {key: 'lowest_price', source: 'price', default: null},
        {key: 'mpn', source: 'model_number', default: null},
        {key: 'offer_count', source: 'offers_json.length', default: 0},
        {key: 'price_worthiness', source: 'price_level_number', default: 0},
        {key: 'quantity', default: 1},
        {key: 'retailer1', source: 'top_retailer', default: {}},
        {key: 'reviews_rating', source: 'review_score', default: null},
        {key: 'reviews_count', source: 'review_count', default: 0},
        {key: 'savings', source: 'energy_savings_fit_enervee', default: null},
        {key: 'short_name', source: 'display_name', default: null},
    ];
    const incentiveParams = [
        {key: 'id', default: null},
        {key: 'amount', default: 0},
        {key: 'type', source: 'business_rule_codes', default: []},
        {key: 'end_date', default: null},
        {key: 'start_date', default: null}
    ];
    const defaultClickProps = {eventType: 'click'};
    const defaultButtonProps = Object.assign({trigger: 'button'}, defaultClickProps);
    const productParamsForTracking = UtilService.getSiteSetting('segment_tracking_product_details', defaultProductParams);
    const segmentDisabled = $location.search().segment_enabled === "false";
    const rootSegmentUrl = ProductApiConsumerService.getFullUrl('api/1.0/log-segment/');
    let knownBotUserAgents = UtilService.getSiteSetting('known_bot_user_agents', []);
    /**
     * Loops over user agents and escapes characters that interfere with regex, then
     * replaces any wildcard (*) characters with .* in order to match any amount of characters.
     * 
     * for example, we could use the following user agent with wild cards in place of specific versions: 
     *   Mozilla/* (X11; Linux x86_64) AppleWebKit/* (KHTML, like Gecko) Chrome/* Safari/*
     * 
     * regexified, this would become:
     *   Mozilla\/.* \(X11; Linux x86_64\) AppleWebKit\/.* \(KHTML, like Gecko\) Chrome\/.* Safari\/.*
     */
    knownBotUserAgents = knownBotUserAgents.map(agent => RegExp(agent.replace(/[-\/\\^$+?.()|[\]{}]/g, '\\$&').replace(/\*/g, '.*')));
    
    const knownUserIPs = UtilService.getSiteSetting('known_user_ip', []);

    let organizationName = UtilService.getSiteSetting('segment_organization_name', '');
    if (organizationName === null) {
        organizationName = UtilService.getSiteSetting('title', '');
    }

    /**
     * Helper function to get default product info to keep things consistent. Adds
     * one or more products to params to be passed to segment.
     *
     * @param {Object|Array<Object>} products one or more products that should be tracked
     * @returns {Array<Object>}
     */
    let getIncentiveParams = function (product) {
        let incentives = product.incentives || product.rebates || [];
        incentives = UtilService.forceArray(incentives);
        return incentives.map(function (incentive) {
            let newIncentiveInfo = {};
            incentiveParams.forEach(function (param) {
                let source = param.source || param.key;
                newIncentiveInfo[param.key] = StorageService.helpers.get(incentive, source, {
                    defaultValue: param.default,
                });
            });
            return newIncentiveInfo;
        });
    };

    /**
     * Helper function to get default product info to keep things consistent. Adds
     * one or more products to params to be passed to segment.
     *
     * @param {Object|Array<Object>} products one or more products that should be tracked
     * @returns {Array<Object>}
     */
    let getProductParams = function (products) {
        products = UtilService.forceArray(products);
        products.filter((product) => !!product);
        return products.map(function (product) {
            let newProductInfo = {};
            productParamsForTracking.forEach(function (param) {
                let source = param.source || param.key;
                newProductInfo[param.key] = StorageService.helpers.get(product, source, {
                    defaultValue: param.default,
                });
            });
            newProductInfo.incentives = getIncentiveParams(product);
            return newProductInfo;
        });
    };

    /**
     * Overrides the method for getting user details.
     *
     * @param {Function} func a function which can be called with no parameters that
     * returns an object of user details.
     */
    _pub.registerUserDetailsFunction = function (func) {
        getUserDetails = function getUserDetails() {
            return $q.resolve(func());
        };
    };

    /**
     * Overrides the method for getting user status.
     *
     * @param {Function} func a function which can be called with no parameters that
     * returns a string for the user's status.
     */
    _pub.registerUserStatusFunction = function (func) {
        getUserStatus = function getUserStatus() {
            return $q.resolve(func());
        };
    };

    /**
     * Report an analytics event. If there are multiple products related to the event, they
     * are fired off as "meta" events from our backend followed by the main event.
     *
     * @param {String} eventName the specific event being tracked
     * (e.g. "Clicked IconBenefitsModal", "Affiliate Click").
     * @param {Object} params
     * @param {Object|Array<Object>} products one or more products that should be tracked
     * @return {Promise} resolves once the track call to Segment is completed
     */
    _pub.reportEvent = function(eventName, params, products) {
        if (segmentDisabled) {
            return $q.resolve();
        }
        params = params || {};
        params.attributes = params.attributes || {};
        if (products) {
            products = UtilService.forceArray(products);
        } else {
            products = [];
        }

        params.product_ids = UtilService.reduceToProperty(products, 'id');

        if (products.length) {
            // Add any product params that are marked to be included in the event as well as the "meta"
            let paramsByProduct = getProductParams(products);
            productParamsForTracking.forEach(function (param) {
                if (param.include_in_event) {
                    params[param.key] = UtilService.reduceToProperty(paramsByProduct, param.key);
                }
            });
        }

        params.category_id = params.category_id || getResolvedDataByKey('activeCategory.id');

        return applyDefaultParams(params).then(function (params) {
            return scheduleReportProducts(products, params._eventId).then(function () {
                return params;
            });
        }).then(function (params) {
            return track(eventName, params);
        });
    };

    // Named reporting events with default properties
    _pub.reportClickEvent = getReportFunctionWithDefaultProps(defaultClickProps);
    _pub.reportButtonEvent = getReportFunctionWithDefaultProps(defaultButtonProps);

    _pub.trackReamazeEvent = trackReamazeEvent;

    /**
     * Method used to track user. Currently only enabled with segment analytics.
     *
     * @param {Object} userData - object of optional attributes to associate with the given user
     * @param {String} userData.email - the user's email
     * @param {Date} userData.created_at - the date and time the user was created at
     * @param {Boolean} userData.verified_user - whether or not the user is a verified utility customer
     * @param {String} userData.signup_source - where the user triggered the signup process
     * @param {String} userData.first_name - the user's first name
     * @param {String} userData.last_name - the user's last name
     * @param {Number} userData.logins - total times the user has logged in
     * @param {Number} userData.user_utility - id of the site or utility the user has chosen
     * @param {String} userData.mileage - the user's annual mileage
     * @return {Promise} resolves once the identify call to Segment is completed
     */
    _pub.trackUser = function (userData, product) {
        if (segmentDisabled) {
            return $q.resolve();
        }
        userData = userData || {};
        return getUserDetails().then(function success(user) {
            return identify(user.id, userData).then(function () {
                $window.analytics.identify(user.id, userData);
            });
        }, function error() {
            return identify(null, userData);
        }).then(function () {
            if (Object.keys(userData).length) {
                return _pub.reportEvent('Account Changed', userData, product);
            }
        });
    };

    /**
     * Method used to track user. Currently only enabled with segment analytics.
     *
     * @param {Object} anonymousUserData - object of optional attributes to associate with the given user
     * @return {Promise} resolves once the identify call to Segment is completed
     */
    _pub.trackAnonymousUser = function (anonymousUserData) {
        if (segmentDisabled || !$window.analytics) {
            return $q.resolve();
        }
        let userId = (anonymousUserData.userId) ? parseInt(anonymousUserData.userId) : null;
        return identify(userId, anonymousUserData).then(function () {
            if (userId !== null) {
                $window.analytics.identify(userId, anonymousUserData);
            }

        });
    };

    /**
     * Method used for tracking "page" events.
     *
     * @param {Object} from
     * @param {Object} from.state the `fromState` parameter from the `$stateChangeSuccess` event
     * @param {Object} from.params the `fromParams` parameter from the `$stateChangeSuccess` event
     */
    _pub.trackPage = function (from) {
        if (segmentDisabled || !$window.analytics) {
            return $q.resolve();
        }
        let referrer = $document.referrer;
        if (!referrer && from.state.name) {
            referrer = $location.protocol() + '://' + $location.host() + $state.href(from.state, from.params);
        }

        _pub.isKnownBotTraffic().then(function (knownBotTraffic) {
            if (!knownBotTraffic) {
                $window.analytics.page({
                    referrer: referrer,
                    search: UtilService.queryStringToDict($window.location.search),
                    type: UtilService.getPageType(),
                    category_id: getResolvedDataByKey('activeCategory.id')
                });

            }
        });
    };

    /**
     * Resets the current userId
     */
    _pub.resetSegmentUser = function () {
        if (segmentDisabled || !$window.analytics) {
            return $q.resolve();
        }
        $window.analytics.reset();
    };

    /**
     * Helper function to normalize event parameters for Segment.
     * Needed because of events previously reported through another
     * method that didn't normalize the params passed.
     * Adds values automatically set by the AnalyticsService.
     *
     * @param {Object} params the parameters to be normalized
     * @return {Promise} resolves with an updated params object
     */
    function applyDefaultParams(params) {
        let defaultParams = {
            eventType: 'interaction',
            location: 'mainContent',
        };
        // Flatten the object by assigning the keys of the attributes
        // property to the main object
        params = Object.assign(params, params.attributes);
        delete params.attributes;
        // Apply params argument _over_ default params
        // Angular.merge is deprecated, but won't be removed in AngularJS. The edge cases
        // don't apply to what we're doing here.
        params = angular.merge({}, defaultParams, params);
        // Add a UUID to the event in case there are related "meta" events
        // we want to associate with it.
        params._eventId = UUIDService.generate();
        return getUserStatus().then(function (userStatus) {
            // Add user status to the properties
            params.userStatus = userStatus;
            // Add page type to the properties
            params.pageType = UtilService.getPageType();
            return params;
        });
    }

    /**
     * Helper function to get data from the current state's resolve data by key
     * @param key {String} - the name of the data to get from the resolve data in dot formation.
     * I.E. activeCategory.id
     * @returns {*} - the resolved data requested
     */
    function getResolvedDataByKey(key) {
        let resolveData = $state.$current.locals.resolve.$$values;
        return StorageService.helpers.get(resolveData, key, {
            defaultValue: null,
        });
    }

    /**
     * Reports separate "meta" events for products, each with a property for reference back
     * to a main event.
     *
     * @param {Array<Object>} products
     * @param {String} parentEventId UUID for the parent event
     * @return {Promise} resolves once all the tracking events have completed
     */
    function reportProducts(products, parentEventId) {
        products = getProductParams(products);
        return $q.all(products.map(product => {
            product._parentEventId = parentEventId;
            return track('_metaProductEvents', product);
        }));
    }

    /**
     * Reports separate "meta" events for products, each with a property for reference back
     * to a main event.
     *
     * @param {Array<Object>} products
     * @param {String} parentEventId UUID for the parent event
     * @return {Promise} immediately if no products or waiting for rebates otherwise
     * once all the tracking events have completed
     */
    function scheduleReportProducts(products, parentEventId) {
        if (!products || products.length === 0) {
            return $q.resolve();
        }

        // TODO: try to get Segment's "batch" API endpoint working so that
        // this gets handled in one request instead of 4–20.
        const firstProduct = products[0];
        if (firstProduct.rebatesLoading === true) {
            const interval = $interval(() => {
                if (firstProduct.rebatesLoading === false) {
                    $interval.cancel(interval);
                    return reportProducts(products, parentEventId)
                }
            }, 50);
            return $q.resolve();
        } else {
            return reportProducts(products, parentEventId);
        }
    }

    /**
     * We want to check for matches in user agents so that we can collectively
     * get every user agent that has Prerender in it. We also want to check for wild cards
     * so we don't have to add new user agents every time a browser is updated.
     * 
     * @param {String} currentUserAgent 
     */
    function checkBotAgents(currentUserAgent) {
        return knownBotUserAgents.some(agent => agent.test(currentUserAgent));
    }

    /**
     * Check to see if the current user's User Agent is in the list of known bot user agents
     * oruser IP in list of know IPs.
     * @returns {promise}
     */
    _pub.isKnownBotTraffic = function() {
        const currentUserAgent = $window.navigator.userAgent;
        // check if the full user agent string exists
        if (checkBotAgents(currentUserAgent)) {
            return $q.resolve(true);
        }
        return GeoService.getCurrentIPAddress().then(
            function (data) {
                if (data) {
                   return knownUserIPs.includes(data.ip);
                }
                return false;
            }, function error() {
                return false;
            }
        );
    };

    /**
     * Promise wrap for the analytics.track method
     *
     * @param {String} eventName
     * @param {Object} properties
     * @return {Promise} resolves when the Segment callback fires
     */
    function track(eventName, properties) {
        return _pub.isKnownBotTraffic().then(function (knownBotTraffic) {
            if (knownBotTraffic || !$window.analytics) {
                return $q.resolve();
            }
            return $q(function (resolve) {
                $window.analytics.track(eventName, properties, {}, function () {
                    resolve();
                });
            });
        });
    }

    /**
     * Promise wrap for the analytics.identify method
     *
     * @param {String|Number} userId
     * @param {Object} properties
     * @return {Promise} resolves when the Segment callback fires
     */
    function identify(userId, properties) {
        return _pub.isKnownBotTraffic().then(function (knownBotTraffic) {
            if (knownBotTraffic) {
                return $q.resolve();
            }
            let data = {
                        'type': 'identify',
                        'user_id': userId,
                        'data': properties
                    };

            const segment_anon_user_id = $window.analytics.user().anonymousId();
            if (segment_anon_user_id !== undefined) {
                data.segment_anon_user_id = segment_anon_user_id;
            }

            return $q(function (resolve) {
                $http.post(
                    rootSegmentUrl,
                    data,
                ).then(function () {
                    resolve();
                });
            });
        });
    }

    function getUserDetailsPlaceholder() {
        $log.warn(
            "AnalyticsService's internal getUserDetails has not been configured! Please call " +
            "AnalyticsService.registerUserDetailsFunction() and pass it a method " +
            "for getting user details"
        );
        return {};
    }

    function getUserStatusPlaceholder() {
        $log.warn(
            "AnalyticsService's internal getUserStatus has not been configured! Please call " +
            "AnalyticsService.registerUserStatusFunction() and pass it a method " +
            "for getting user details"
        );
        return {};
    }

    function getReportFunctionWithDefaultProps(defaultProps) {
        return function reportEventWithDefaults(action, props, products) {
            props = angular.merge({}, defaultProps, props);
            return _pub.reportEvent(action, props, products);
        };
    }

    /**
     * Tracks Reamaze events
     *
     * @param data
     */
    function trackReamazeEvent(data) {
        _pub.reportEvent(data.name, {}, null);
    }

    return _pub;
}

module.exports = AnalyticsService;
